import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  type EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
} from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  type MsalGuardConfiguration,
  MsalInterceptor,
  type MsalInterceptorConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  type AuthenticationResult,
  BrowserCacheLocation,
  type IPublicClientApplication,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  AuthInjectorToken,
  AuthStore,
  ProfileRequestCoordinator,
} from '@clean-code/shared/auth/util-auth';
import { firstValueFrom } from 'rxjs';
import { AUTH_CONFIG } from './model/auth-config.token';
import type { AzureConfig } from './model/azure-config';
import { AzureAuthService } from './services/azure-auth.service';

export interface AzureAuthOptions {
  /**
   * Route MsalGuard navigates to when an interactive login fails. It has to be
   * a route that is *not* protected by MsalGuard, otherwise the failed login
   * bounces back into the guard and retries forever.
   */
  loginFailedRoute?: string;
  /**
   * Scopes requested during interactive login. Defaults to `user.read`.
   * API scopes from `protectedResourceMap` are acquired silently afterward.
   */
  loginScopes?: string[];
}

const DEFAULT_LOGIN_FAILED_ROUTE = '/login';
const DEFAULT_LOGIN_SCOPES = ['user.read'];

export function loggerCallback(_logLevel: LogLevel, message: string): void {
  console.debug(message);
}

export function MSALInstanceFactory(
  config: AzureConfig,
): IPublicClientApplication {
  return new PublicClientApplication({
    auth: config.auth,
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(
  config: AzureConfig,
): MsalInterceptorConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: config.protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(
  config: AzureConfig,
  options: AzureAuthOptions = {},
): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      // Interactive login establishes the MSAL session. API scopes are
      // acquired silently via MsalInterceptor / acquireTokenSilent.
      scopes: options.loginScopes ?? DEFAULT_LOGIN_SCOPES,
    },
    loginFailedRoute: options.loginFailedRoute ?? DEFAULT_LOGIN_FAILED_ROUTE,
  };
}

async function initializeMsal(
  msalService: MsalService,
  authStore: InstanceType<typeof AuthStore>,
  profileCoordinator: ProfileRequestCoordinator,
): Promise<void> {
  try {
    // Use MsalService.handleRedirectObservable — not instance.handleRedirectPromise().
    // MSAL caches redirect handling by hash key ("" vs "#code=..."). MsalGuard
    // later passes the hash captured when MsalService was constructed; a prior
    // no-hash call redeems the auth code twice → token endpoint 400 →
    // "Guard - error while logging in, unable to activate".
    const result = await firstValueFrom(
      msalService.handleRedirectObservable(),
    );

    setActiveAccount(msalService, result);

    if (result?.accessToken) {
      authStore.setAccessToken(result.accessToken);
      profileCoordinator.reset();
    } else if (msalService.instance.getAllAccounts().length > 0) {
      profileCoordinator.reset();
    }
  } catch (error) {
    // An interrupted redirect must not break bootstrap; MsalGuard starts a
    // fresh interactive login instead.
    console.error('MSAL initialization failed', error);
  }

  authStore.loadFromStorage();
}

function setActiveAccount(
  msalService: MsalService,
  result: AuthenticationResult | null,
): void {
  if (result?.account) {
    msalService.instance.setActiveAccount(result.account);
    return;
  }

  if (msalService.instance.getActiveAccount()) {
    return;
  }

  const [account] = msalService.instance.getAllAccounts();

  if (account) {
    msalService.instance.setActiveAccount(account);
  }
}

/**
 * Registers MSAL for an application, replacing `AzureAuthModule.forRoot()`.
 * Usable from `bootstrapApplication` providers as well as `NgModule.providers`.
 */
export function provideAzureAuth(
  config: AzureConfig,
  options: AzureAuthOptions = {},
): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: AUTH_CONFIG, useValue: config },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [AUTH_CONFIG],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: (authConfig: AzureConfig) =>
        MSALGuardConfigFactory(authConfig, options),
      deps: [AUTH_CONFIG],
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [AUTH_CONFIG],
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    AzureAuthService,
    { provide: AuthInjectorToken, useExisting: AzureAuthService },
    provideAppInitializer(() => {
      // Construct before handleRedirectPromise so token-success subscribers
      // exist if broadcast events are emitted during redirect processing.
      inject(AzureAuthService);
      return initializeMsal(
        inject(MsalService),
        inject(AuthStore),
        inject(ProfileRequestCoordinator),
      );
    }),
  ]);
}
